<template>
  <div class="game-quest-view" :class="{ 'with-side-panel': hasHero, 'brief-open': briefOpen }">
    <div class="quest-header-section">
      <h1>Operations Center</h1>
      <div class="quest-brief">
        <p class="subtitle">Manage contracts and oversee ongoing expeditions.</p>
        <button
          type="button"
          class="brief-info-btn"
          :aria-expanded="briefOpen"
          aria-label="Contract info"
          @click="briefOpen = !briefOpen"
        >
          <i class="fas fa-info-circle"></i>
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      Scanning for signals...
    </div>

    <div v-else-if="!hasHero" class="no-hero-banner">
      <h2>Hero Required</h2>
      <p>
        Quests cannot be started or completed until you activate your first hero.
        Visit the Hero tab to recruit one, then return here for contracts within
        three levels of your hero.
      </p>
    </div>

    <template v-else>
      <p class="hero-level-hint">
        Showing contracts for hero level {{ heroLevel }} (±{{ levelRange }}).
        Chain steps can appear outside this range until a matching hero is assigned.
      </p>

      <div class="quest-tabs">
        <button
          :class="['tab-btn', { active: activeTab === 'available' }]"
          @click="activeTab = 'available'"
        >
          Available Contracts
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'active' }]"
          @click="activeTab = 'active'"
        >
          Active Missions
        </button>
      </div>

      <div class="quest-list-container">
        <div v-if="activeTab === 'available'" class="quest-list">
          <p v-if="availableQuests.length === 0" class="empty-msg">
            No contracts currently available in this sector for your hero's level.
          </p>
          <QuestItem
            v-for="quest in availableQuests"
            :key="quest.slug"
            :quest="quest"
            :player-level="heroLevel"
            @prepare-expedition="handleStartQuest"
          />
        </div>

        <div v-else class="quest-list">
          <p v-if="activeQuests.length === 0" class="empty-msg">
            No operations currently in progress.
          </p>
          <QuestItem
            v-for="quest in activeQuests"
            :key="quest.id"
            :quest="quest"
            :player-level="heroLevel"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useGameStore } from "../store/game";
import QuestItem from "../components/QuestItem.vue";

function tabFromQuery(tab) {
  return tab === "active" ? "active" : "available";
}

const route = useRoute();
const gameStore = useGameStore();
const activeTab = ref(tabFromQuery(route.query.tab));
const briefOpen = ref(false);

watch(
  () => route.query.tab,
  (tab) => {
    activeTab.value = tabFromQuery(tab);
  }
);
const loading = ref(false);
const hasHero = ref(false);
const heroLevel = ref(1);
const levelRange = 3;

const availableQuests = computed(() => gameStore.availableQuests);
const activeQuests = computed(() => gameStore.activeQuests);

const handleStartQuest = async (payload) => {
  try {
    const quest = availableQuests.value.find((q) => q.slug === payload.questSlug);
    await gameStore.startQuest({
      questSlug: payload.questSlug,
      addedGold: payload.totalGold - (quest?.min_gold_cost || 0),
      assigned_hero_id: payload.assigned_hero_id,
      assigned_troops: payload.assigned_troops || {},
    });
    activeTab.value = "active";
  } catch (error) {
    console.error("Failed to start quest:", error);
    alert(error.response?.data?.error || error.message || "Failed to start quest");
  }
};

onMounted(async () => {
  loading.value = true;
  try {
    await gameStore.fetchPlayerAssets();

    const availableRes = await gameStore.fetchAvailableQuests();
    if (availableRes) {
      hasHero.value = !!availableRes.hasHero;
      if (availableRes.heroLevel != null) {
        heroLevel.value = availableRes.heroLevel;
      } else if (availableRes.playerLevel != null) {
        heroLevel.value = availableRes.playerLevel;
      } else if (gameStore.activeHero?.level) {
        heroLevel.value = gameStore.activeHero.level;
        hasHero.value = true;
      }
    } else if (gameStore.playerHeroes?.length > 0) {
      hasHero.value = true;
      heroLevel.value = gameStore.activeHero?.level || 1;
    }

    await Promise.all([
      gameStore.fetchActiveQuests(),
      gameStore.fetchPlayerResources(),
      gameStore.fetchPlayerCards(),
      gameStore.activeHero?.id
        ? gameStore.fetchHeroSockets(gameStore.activeHero.id)
        : Promise.resolve(),
    ]);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.game-quest-view {
  padding: 1rem;
  max-width: 800px;
  margin: 0 auto;
  color: #fff;
  padding-bottom: 80px;
}

.game-quest-view.with-side-panel {
  padding-right: 370px;
  max-width: none;
}

.quest-header-section {
  text-align: center;
  margin-bottom: 2rem;
}

h1 {
  font-family: "Cinzel", serif;
  font-size: 2rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
}

.subtitle {
  color: #88c0d0;
  font-size: 0.9rem;
  margin: 0;
}

.quest-brief {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.brief-info-btn {
  display: none;
}

.no-hero-banner {
  text-align: center;
  padding: 2.5rem 1.5rem;
  margin: 1rem auto;
  max-width: 520px;
  background: rgba(20, 30, 45, 0.85);
  border: 1px solid #3d5a73;
  border-radius: 10px;
}

.no-hero-banner h2 {
  margin: 0 0 0.75rem;
  color: #ffcc66;
  font-size: 1.25rem;
}

.no-hero-banner p {
  margin: 0;
  color: #aab8c5;
  line-height: 1.5;
  font-size: 0.95rem;
}

.hero-level-hint {
  text-align: center;
  color: #88c0d0;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}

.quest-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  border-bottom: 1px solid #333;
}

.tab-btn {
  background: transparent;
  border: none;
  padding: 1rem 2rem;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  border-bottom: 2px solid transparent;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.tab-btn:hover {
  color: #ddd;
}

.tab-btn.active {
  color: #00f0ff;
  border-bottom: 2px solid #00f0ff;
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);
}

.quest-list-container {
  min-height: 300px;
}

.quest-list {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  box-sizing: border-box;
}

.loading-state {
  text-align: center;
  padding: 2rem;
  color: #00f0ff;
  font-style: italic;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
}

.empty-msg {
  text-align: center;
  color: #555;
  margin-top: 2rem;
  font-style: italic;
}

@media (max-width: 768px) {
  .game-quest-view.with-side-panel {
    padding-right: 1rem;
    max-width: 800px;
  }

  .quest-header-section {
    margin-bottom: 0.65rem;
  }

  h1 {
    font-size: 1.15rem;
    margin-bottom: 0.25rem;
    letter-spacing: 1px;
  }

  .subtitle {
    font-size: 0.78rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100vw - 4.5rem);
  }

  .game-quest-view.brief-open .subtitle {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }

  .brief-info-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: 1px solid rgba(0, 246, 255, 0.4);
    border-radius: 50%;
    color: #88c0d0;
    cursor: pointer;
    font-size: 0.9rem;
  }

  .hero-level-hint {
    display: none;
    font-size: 0.78rem;
    margin: 0.45rem 0 0.65rem;
  }

  .game-quest-view.brief-open .hero-level-hint {
    display: block;
  }

  .quest-tabs {
    margin-bottom: 0.75rem;
  }

  .tab-btn {
    padding: 0.55rem 0.85rem;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
  }
}
</style>
